// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { DealerHomePage, UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { DealStatuses, JacketType } from '../interfaces/deal.interfaces';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    constructor(private readonly _userService: UserService) {}

    canDeleteDeal(status: DealStatuses, statuses: DealStatuses[], jacketType: JacketType): boolean {
        const hasCorrectStatus = statuses.some((s) => s === status);
        let hasPermission = false;

        switch (jacketType) {
            case JacketType.Deal: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.DealsDelete]);

                break;
            }
            case JacketType.Document: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.ConsumerDocumentsDelete]);

                break;
            }
            case JacketType.Storage: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.DocumentStorageDelete]);
                break;
            }
            default: {
                break;
            }
        }

        return hasCorrectStatus && hasPermission;
    }

    canCloseArchiveFinalizeDeal(status: DealStatuses, statuses: DealStatuses[], jacketType: JacketType): boolean {
        const hasCorrectStatus = statuses.some((s) => s === status);
        let hasPermission = false;

        switch (jacketType) {
            case JacketType.Deal: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.DealsFinalizeAndClose]);

                break;
            }
            case JacketType.Document: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.ConsumerDocumentsCloseAndArchive]);

                break;
            }
            case JacketType.Storage: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.DocumentStorageCloseAndArchive]);
                break;
            }
            default: {
                break;
            }
        }

        return hasCorrectStatus && hasPermission;
    }

    canModifyDocuments(status: DealStatuses, statuses: DealStatuses[], jacketType: JacketType): boolean {
        const hasCorrectStatus = statuses.some((s) => s === status);
        let hasPermission = true;

        switch (jacketType) {
            case JacketType.Deal: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.Deals]);

                break;
            }
            case JacketType.Storage: {
                hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.DocumentStorage]);
                break;
            }
            default: {
                hasPermission = true;
                break;
            }
        }

        return hasCorrectStatus && hasPermission;
    }

    get canStartDealJacket(): boolean {
        return this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.Deals]);
    }

    hasPermission(userPermission: UserPermissionIds): boolean {
        let hasPermission = false;

        if (userPermission === undefined || userPermission === null) {
            //If Constant Does Not Have Permission to Find It Is Assumed That No Permission is Required
            hasPermission = true;
        } else {
            hasPermission = this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [userPermission]);
        }

        return hasPermission;
    }

    routeToDealerHomePage() {
        //ToDo: This needs to be added to the DefaultPage table to be truely dynamic and table driven. Several apis will need to be updated to allow for this.

        switch (this._userService.user.defaultPageId) {
            case DealerHomePage.deals: {
                if (this._userService.hasPermission$([UserTypes.Dealer, UserTypes.Internal], [UserPermissionIds.Deals])) {
                    return ['/dashboard', 'deals'];
                } else {
                    return ['/dashboard', 'leads'];
                }
            }
            case DealerHomePage.consumerDocuments: {
                return ['/dashboard', 'documents'];
            }
            case DealerHomePage.leads: {
                return ['/dashboard', 'leads'];
            }

            default:
                return ['/dashboard', 'leads'];
        }
    }
}
